import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ContactInput, Contact } from '../types';
import { apiService } from '../services/api';
import { validatePhone } from '../utils/phoneUtils';
import { Sheet } from './ui/Sheet';
import { AvatarUpload } from './AvatarUpload';
import { PhoneInput } from './PhoneInput';

interface Category {
  id: string;
  nome: string;
}

const contactSchema = z.object({
  avatarUrl: z.string().optional(),
  nome: z.string().min(1, 'Nome é obrigatório'),
  companyName: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  observacoes: z.string().optional(),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, {
    message: 'Formato de telefone inválido',
  }),
  categoriaId: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
  onCancel: () => void;
  onAddCategory?: () => void;
  categoriesVersion?: number;
}

export function ContactForm({ contact, onSuccess, onCancel, onAddCategory, categoriesVersion }: ContactFormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      avatarUrl: contact?.avatarUrl || '',
      nome: contact?.nome || '',
      companyName: contact?.companyName || '',
      telefone: contact?.telefone || '',
      email: contact?.email || '',
      observacoes: contact?.observacoes || '',
      categoriaId: contact?.categoriaId || '',
    },
  });

  const avatarUrl = watch('avatarUrl');
  const nome = watch('nome');

  useEffect(() => {
    loadCategories();
  }, [categoriesVersion]);

  // Reset form when contact changes AND categories are loaded
  useEffect(() => {
    if (contact && !loadingCategories) {
      reset({
        avatarUrl: contact.avatarUrl || '',
        nome: contact.nome || '',
        companyName: contact.companyName || '',
        telefone: contact.telefone || '',
        email: contact.email || '',
        observacoes: contact.observacoes || '',
        categoriaId: contact.categoriaId || '',
      });

      // Força o setValue após reset para garantir que o select seja atualizado
      setTimeout(() => {
        if (contact.categoriaId) {
          setValue('categoriaId', contact.categoriaId);
        }
      }, 100);
    }
  }, [contact, categories, loadingCategories, reset, setValue]);

  const loadCategories = async () => {
    try {
      const data = await apiService.getCategories();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: data.telefone,
        email: data.email || undefined,
        observacoes: data.observacoes || undefined,
        categoriaId: data.categoriaId || undefined,
        avatarUrl: data.avatarUrl || undefined,
        companyName: data.companyName || undefined,
      };

      if (contact) {
        await apiService.updateContact(contact.id, contactInput);
        toast.success('Contato atualizado com sucesso');
      } else {
        await apiService.createContact(contactInput);
        toast.success('Contato criado com sucesso');
      }

      onSuccess();
    } catch (err: unknown) {
      // Verificar se é erro de quota
      interface QuotaError {
        isQuotaError?: boolean;
        upgradeRequired?: boolean;
        message?: string;
      }
      
      const error = err as QuotaError;
      if (error?.isQuotaError || error?.upgradeRequired) {
        toast.error(error.message || 'Limite de contatos atingido. Faça upgrade do seu plano para continuar.', {
          duration: 6000,
          icon: '⚠️'
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar contato';
      toast.error(errorMessage);
    }
  };

  return (
    <Sheet
      isOpen={true}
      onClose={onCancel}
      title={contact ? 'Editar Contato' : 'Novo Contato'}
      description={contact ? `Alterando detalhes de ${contact.nome.toUpperCase()}` : 'Preencha os dados para criar um novo contato'}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">

        {/* Avatar Section */}
        <div className="flex flex-col items-center justify-center p-8 bg-white/5 rounded-[2rem] border border-white/10 space-y-4">
          <AvatarUpload
            currentAvatarUrl={avatarUrl}
            onAvatarChange={(url) => setValue('avatarUrl', url)}
            name={nome}
          />
          <div className="text-center">
            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Foto do Contato</h4>
            <p className="text-[9px] text-gray-600 font-bold uppercase tracking-widest mt-1">PNG, JPG até 2MB</p>
          </div>
        </div>

        <div className="space-y-8">
          <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
            <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
            Informações Pessoais
          </h3>
          
          <div className="space-y-6">
            <div className="space-y-2.5">
              <label htmlFor="nome" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                Nome Completo
              </label>
              <input
                id="nome"
                type="text"
                {...register('nome')}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                placeholder="Ex: João Silva"
              />
              {errors.nome && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1 ml-1">{errors.nome.message}</p>
              )}
            </div>

            <div className="space-y-2.5">
              <label htmlFor="companyName" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                Empresa
              </label>
              <input
                id="companyName"
                type="text"
                {...register('companyName')}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                placeholder="Ex: Conexões Soluções"
              />
            </div>
          </div>
        </div>

        <div className="space-y-8 pt-10 border-t border-white/5">
          <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
            <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
            Contatos e Categoria
          </h3>

          <div className="space-y-6">
            <div className="space-y-2.5">
              <label htmlFor="telefone" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                Número de Telefone
              </label>
              <PhoneInput
                id="telefone"
                {...register('telefone')}
                error={errors.telefone?.message}
                placeholder="+55 11 99999-9999"
              />
            </div>

            <div className="space-y-2.5">
              <label htmlFor="email" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                Endereço de E-mail
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                placeholder="joao@exemplo.com"
              />
              {errors.email && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1 ml-1">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2.5">
              <label htmlFor="categoriaId" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                Categoria
              </label>
              <div className="flex gap-3">
                <select
                  id="categoriaId"
                  {...register('categoriaId')}
                  className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:border-blue-500 outline-none transition-all appearance-none"
                  disabled={loadingCategories}
                >
                  <option value="" className="bg-[#1e293b]">Selecione uma categoria...</option>
                  {loadingCategories ? (
                    <option disabled className="bg-[#1e293b]">Carregando categorias...</option>
                  ) : (
                    categories.map((category) => (
                      <option key={category.id} value={category.id} className="bg-[#1e293b]">
                        {category.nome}
                      </option>
                    ))
                  )}
                </select>
                {onAddCategory && (
                  <button
                    type="button"
                    onClick={onAddCategory}
                    className="w-[58px] bg-blue-600/10 text-blue-400 rounded-2xl border border-blue-500/20 hover:bg-blue-600/20 transition-all flex items-center justify-center text-xl shadow-lg"
                    title="Nova Categoria"
                  >
                    +
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8 pt-10 border-t border-white/5">
          <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
            <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
            Informações Adicionais
          </h3>
          
          <div className="space-y-2.5">
            <label htmlFor="observacoes" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
              Observações / Descrição
            </label>
            <textarea
              id="observacoes"
              {...register('observacoes')}
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner resize-none"
              placeholder="Alguma observação relevante sobre este contato..."
            />
          </div>
        </div>

        {/* Perfex Info */}
        {contact?.perfexLeadId && (
          <div className="bg-blue-600/10 border border-blue-500/20 rounded-[2rem] p-8 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] font-oswald">Importado do Perfex CRM</h4>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Lead ID</span>
              <span className="px-3 py-1 bg-white/5 rounded-lg text-white font-mono text-xs border border-white/10">{contact.perfexLeadId}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-10 border-t border-white/10 flex gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-10 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 border border-blue-500 relative overflow-hidden group/btn font-oswald disabled:opacity-50"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
            <span>{isSubmitting ? 'Salvando...' : 'Salvar Contato'}</span>
          </button>
        </div>
      </form>
    </Sheet>
  );
}
